/**
 * AgentAddonDialog
 *
 * Turn on agent support (BR-100..BR-105, JR-9): shows today's prorated amount with its date
 * range and the ongoing monthly amount BEFORE consent, then charges the saved card once.
 *
 *   loading       skeleton while the server quote resolves
 *   not eligible  trial / plan without the add-on / inactive subscription: explained, no charge
 *   card step     no saved card: CardSetupFlow (nothing is charged), then the summary
 *   summary       Today / From <date> rows + note + consent; primary disabled until ticked
 *   charging      every control disabled, one request in flight
 *   declined      reason, "Nothing was charged"; "Use a different card"
 *
 *   unreconciled  ADDON_CHARGE_UNRECONCILED (BR-102): an earlier attempt is being confirmed; never invites
 *                 a second charge
 *
 * Centered modal >= 768px, bottom sheet below. The idempotency key is persisted per subscription
 * (lib/charge-idempotency): a reload or reopened dialog retries an UNRESOLVED attempt under the same
 * key (BR-91/BR-102); any definitive outcome rotates it.
 */

import { CONSENT_COPY } from '@resida/shared';
import type { AgentAddonIneligibleReason } from '@resida/api-types/billing';
import { peekChargeIdempotencyKey } from '../../lib/charge-idempotency';
import { useAuth } from '../../hooks/useAuth';
import { formatUsd, formatPeriodDate } from '../../api/billing';
import { useAgentAddonQuote, useEnableAgentAddonMutation } from '../../hooks/useBilling';
import { Skeleton } from '../ui/skeleton';
import {
  ChargeActionButton,
  ChargeCancelButton,
  ChargeCardCapture,
  ChargeCardLine,
  ChargeConsent,
  ChargeDeclinedPanel,
  ChargeDialogFooter,
  ChargeDialogShell,
  ChargeInProgressPanel,
  ChargeOutcomeAlerts,
  ChargeQuoteErrorPanel,
  ChargingStatus,
} from './ChargeDialogParts';
import { useChargeFlow } from './useChargeFlow';

const INELIGIBLE_COPY: Record<AgentAddonIneligibleReason, string> = {
  trial: 'Agent support is available once you choose a plan. Your trial includes listings, not agents.',
  zero_price_bundle: 'Agent support is not charged separately on your plan.',
  not_offered: "Your current plan doesn't include agent support.",
  not_active: 'Agent support can be turned on once your subscription is active and up to date.',
  already_enabled: 'Agent support is already on.',
};

/** The outcome specific to the add-on route, on top of the shared charge outcomes. */
type AddonRouteOutcome = { kind: 'ineligible'; reason: AgentAddonIneligibleReason | undefined };

interface AgentAddonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Agent support is on; `message` is ready to show the landlord. */
  onEnabled: (message: string) => void;
}

export function AgentAddonDialog({ open, onOpenChange, onEnabled }: AgentAddonDialogProps) {
  const p = 'agent-addon';
  // A landlord has exactly one subscription, so the manager id identifies it for key scoping.
  const { manager } = useAuth();
  const keyScope = `agent-addon:${manager?.id ?? 'current'}`;
  // The persisted key lets the server return the PINNED quote of an unresolved earlier attempt (BR-102).
  const quoteQuery = useAgentAddonQuote(open, () => peekChargeIdempotencyKey(keyScope));
  const enableMutation = useEnableAgentAddonMutation();

  const flow = useChargeFlow<AddonRouteOutcome>({
    open,
    keyScope,
    onOpenChange,
    refetchQuote: () => void quoteQuery.refetch(),
    unreconciledCode: 'ADDON_CHARGE_UNRECONCILED',
    quotedCard: quoteQuery.data?.defaultPaymentMethod,
    classifyRouteError: (err, details) => {
      switch (err.code) {
        case 'ADDON_NOT_ELIGIBLE':
          return { kind: 'ineligible', reason: details.ineligibleReason as AgentAddonIneligibleReason | undefined };
        case 'ADDON_ALREADY_ACTIVE':
          onEnabled('Agent support is already on.');
          return 'handled';
        default:
          return undefined;
      }
    },
  });
  const { consent, charging, outcome, card, close } = flow;

  const quote = quoteQuery.data;

  const handleEnable = () => {
    if (!quote || !consent) return;
    void flow.charge(
      (idempotencyKey) => enableMutation.mutateAsync({ consentToProratedCents: quote.proratedCents, idempotencyKey }),
      () =>
        onEnabled(
          quote.proratedCents > 0
            ? 'Agent support is on. Receipt emailed. Find it under Settings → Billing.'
            : 'Agent support is on.',
        ),
    );
  };

  const renderBody = (): { title: string; description: string | null; body: React.ReactNode } => {
    const title = 'Turn on agent support';

    if (quoteQuery.isError) {
      return {
        title,
        description: null,
        body: (
          <ChargeQuoteErrorPanel
            testIdPrefix={p}
            message="We couldn't load the price for agent support. Try again in a moment."
            onClose={close}
            onRetry={() => void quoteQuery.refetch()}
          />
        ),
      };
    }

    if (!quote || !flow.sessionFresh) {
      return {
        title,
        description: null,
        body: (
          <div className="flex flex-col gap-3" data-testid={`${p}-loading`} aria-busy="true">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-11 w-full" />
          </div>
        ),
      };
    }

    // Not eligible (from the quote, or a 403 on enable): explain, no charge.
    const ineligibleReason = outcome?.kind === 'ineligible' ? outcome.reason : quote.ineligibleReason;
    if (!quote.eligible || outcome?.kind === 'ineligible') {
      return {
        title: "Agent support isn't available",
        description: ineligibleReason ? INELIGIBLE_COPY[ineligibleReason] : 'Agent support is not available right now.',
        body: (
          <ChargeDialogFooter>
            <ChargeCancelButton testId={`${p}-cancel`} label="Close" onClick={close} />
          </ChargeDialogFooter>
        ),
      };
    }

    const todayText = formatUsd(quote.proratedCents);
    const monthlyText = formatUsd(quote.monthlyCents);
    const rangeText =
      quote.periodStart && quote.periodEnd
        ? `${formatPeriodDate(quote.periodStart)} – ${formatPeriodDate(quote.periodEnd)} (${quote.daysRemaining} day${quote.daysRemaining === 1 ? '' : 's'})`
        : null;

    // Card capture — no saved card, or the landlord chose another one.
    if (flow.capturingCard || !card) {
      return {
        title: 'Add a card to turn on agent support',
        description: null,
        body: (
          <ChargeCardCapture
            testIdPrefix={p}
            handlers={flow.cardCapture}
            summary={
              <p className="text-sm text-muted-foreground m-0 mb-4">
                Nothing is charged yet. We save your card, then ask you to confirm today&apos;s {todayText}.
              </p>
            }
          />
        ),
      };
    }

    if (outcome?.kind === 'declined') {
      return {
        title,
        description: null,
        body: (
          <ChargeDeclinedPanel
            testIdPrefix={p}
            reason={outcome.reason}
            unchangedNote="agent support is still off."
            onClose={close}
            onUseDifferentCard={flow.startCardCapture}
          />
        ),
      };
    }

    if (outcome?.kind === 'in_progress') {
      return {
        title,
        description: null,
        body: (
          <ChargeInProgressPanel testIdPrefix={p} closeTestId={`${p}-cancel`} onClose={close}>
            A payment for agent support is already being processed, maybe in another tab. Wait a moment and refresh —
            you won&apos;t be charged twice.
          </ChargeInProgressPanel>
        ),
      };
    }

    return {
      title,
      description: null,
      body: (
        <div className="flex flex-col gap-5">
          <dl className="rounded-lg border border-border p-4 flex flex-col gap-3 text-sm m-0" data-testid={`${p}-summary`}>
            <div className="flex justify-between gap-3" data-testid={`${p}-today`}>
              <div>
                <dt className="font-semibold text-foreground">Today</dt>
                <dd className="m-0 text-muted-foreground">
                  Rest of this billing period{rangeText ? ` · ${rangeText}` : ''}
                </dd>
              </div>
              <span className="font-semibold text-foreground text-right" data-testid={`${p}-today-amount`}>
                {todayText} + tax
              </span>
            </div>
            <div className="flex justify-between gap-3 border-t border-border pt-3" data-testid={`${p}-monthly`}>
              <div>
                <dt className="font-semibold text-foreground">
                  From {quote.nextBillingDate ? formatPeriodDate(quote.nextBillingDate) : 'your next bill'}
                </dt>
                <dd className="m-0 text-muted-foreground">
                  {quote.unitCount} unit{quote.unitCount === 1 ? '' : 's'} × {formatUsd(quote.unitPriceCents)}, added to
                  your monthly bill
                </dd>
              </div>
              <span className="font-semibold text-foreground text-right" data-testid={`${p}-monthly-amount`}>
                +{monthlyText}/month
              </span>
            </div>
          </dl>

          <p className="m-0 text-[13px] leading-5 text-muted-foreground" data-testid={`${p}-policy`}>
            Agents start right away. Turn off any time — it stays on until the end of that billing period, with no refund
            for unused days.
          </p>

          {quote.resumesEarlierAttempt && (
            <p role="status" className="m-0 text-sm text-muted-foreground" data-testid={`${p}-resumes-note`}>
              This resumes your earlier attempt, so the amount and dates above are the ones from that attempt. The same
              request can&apos;t charge you twice.
            </p>
          )}

          {outcome?.kind === 'price_changed' && (
            <p role="status" className="m-0 text-sm text-warning" data-testid={`${p}-amount-changed`}>
              The amount has changed. Review it and confirm again.
            </p>
          )}
          <ChargeOutcomeAlerts testIdPrefix={p} outcome={outcome} />

          <ChargeCardLine
            testIdPrefix={p}
            card={card}
            charging={charging}
            changeCardTestId={`${p}-change-card`}
            onChangeCard={flow.changeCard}
          />

          <ChargeConsent testId={`${p}-consent`} checked={consent} disabled={charging} onChange={flow.setConsent}>
            {CONSENT_COPY.agent_proration(todayText)}
          </ChargeConsent>

          {charging && <ChargingStatus testIdPrefix={p} card={card} />}

          <ChargeDialogFooter>
            <ChargeCancelButton testId={`${p}-cancel`} label="Cancel" onClick={close} disabled={charging} />
            <ChargeActionButton
              testId={`${p}-confirm`}
              busy={charging}
              busyLabel="Charging…"
              disabled={!consent || charging}
              onClick={handleEnable}
            >
              {quote.proratedCents > 0 ? `Pay ${todayText} & turn on` : 'Turn on agent support'}
            </ChargeActionButton>
          </ChargeDialogFooter>
        </div>
      ),
    };
  };

  const { title, description, body } = renderBody();

  return (
    <ChargeDialogShell
      open={open}
      onOpenChange={flow.guardedOpenChange}
      charging={charging}
      isChargeInFlight={flow.isChargeInFlight}
      testId={`${p}-dialog`}
      title={title}
      description={description}
    >
      {body}
    </ChargeDialogShell>
  );
}
